import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { joinChildren } from "../misc/joinChildren";
import styles from "./ObjectMenu.scss";
import { IconButton } from "../input/IconButton";
import { Dismiss24Regular, EyeOffRegular, EyeRegular, Info24Filled } from "@fluentui/react-icons";
import { Notch } from "./Notch";
import { Button, makeStyles, mergeClasses } from "@fluentui/react-components";
import { EnterOnVRPopover } from "./EnterOnVRPopover";
import { EnterOnDevice } from "./EnterOnDeviceModal";
import { getGateLinkUrl } from "../../utils/gate-link-url";

const useStyles = makeStyles({
  infoButton: {
    display: "flex",
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
    border: "none",
    height: "40px",
    padding: "var(--Vertical-SNudge, 6px) var(--Horizontal-SNudge, 6px)",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: "0",
    borderRadius: "4px",
    background: "var(--toolbar-overlay-background)",
    cursor: "pointer",
    "& svg": {
      color: "var(--colorNeutralForeground1)",
      fill: "currentColor"
    },
    position: "fixed",
    bottom: "20px",
    left: "30px"
  },
  buttonActive: {
    backgroundColor: "var(--colorNeutralBackground3)"
  }
});

export function ObjectMenuButton({ children, className, ...rest }) {
  return (
    <IconButton compactSm className={classNames(styles.objectMenuButton, className)} {...rest}>
      {children}
    </IconButton>
  );
}

ObjectMenuButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

export function ObjectMenu({
  children,
  title,
  onClose,
  onBack,
  onPrevObject,
  onNextObject,
  currentObjectIndex,
  objectCount,
  onToggleLights,
  lightsEnabled,
  hideNotch,
  onOpenArtworkInfo,
  showArtworkInfoButton,
  isArtworkInfoOpen,
  showEnterOnVR,
  enterOnVROpen,
  onEnterOnVROpen,
  onEnterOnVRClose,
  linkCode,
  loadingLinkCode,
  onEnterOnConnectedHeadset,
  attemptLink
}) {
  const classes = useStyles();

  return (
    <>
      {showEnterOnVR && (
        <EnterOnVRPopover open={enterOnVROpen} onOpen={onEnterOnVROpen} onClose={onEnterOnVRClose}>
          <EnterOnDevice
            shortUrl={getGateLinkUrl()}
            loadingCode={loadingLinkCode}
            code={linkCode}
            headsetConnected={AFRAME.utils.device.checkHeadsetConnected()}
            unsupportedBrowser={false}
            onEnterOnConnectedHeadset={onEnterOnConnectedHeadset}
            attemptLink={attemptLink}
          />
        </EnterOnVRPopover>
      )}
      {showArtworkInfoButton && (
        <Button
          appearance="subtle"
          className={!isArtworkInfoOpen ? classes.infoButton : mergeClasses(classes.infoButton, classes.buttonActive)}
          onClick={onOpenArtworkInfo}
          aria-label="Artwork Info"
        >
          <Info24Filled />
        </Button>
      )}
      {!hideNotch && (
        <Notch onClose={onBack} spaced>
          {lightsEnabled ? (
            <Button
              icon={<EyeRegular style={{ width: "18px", height: "18px" }} />}
              onClick={onToggleLights}
              aria-label="Turn Lights Off"
              appearance="transparent"
              size="small"
            />
          ) : (
            <Button
              icon={<EyeOffRegular style={{ width: "18px", height: "18px" }} />}
              onClick={onToggleLights}
              aria-label="Turn Lights On"
              appearance="transparent"
              size="small"
            />
          )}
        </Notch>
      )}
      <div className={styles.objectMenuContainer}>
        {/* <div className={styles.objectMenu}>
          <div className={styles.menu}>
            {joinChildren(children, () => (
              <div className={styles.separator} />
            ))}
          </div>
        </div> */}
        {/* {objectCount > 1 && (
          <div className={styles.pagination}>
            <IconButton onClick={onPrevObject}>
              <ArrowBackIcon width={24} height={24} />
            </IconButton>
            <p>
              {currentObjectIndex + 1}/{objectCount}
            </p>
            <IconButton onClick={onNextObject}>
              <ArrowForwardIcon width={24} height={24} />
            </IconButton>
          </div>
        )} */}
      </div>
    </>
  );
}

ObjectMenu.propTypes = {
  currentObjectIndex: PropTypes.number.isRequired,
  objectCount: PropTypes.number.isRequired,
  onPrevObject: PropTypes.func,
  onNextObject: PropTypes.func,
  children: PropTypes.node,
  title: PropTypes.node,
  onClose: PropTypes.func,
  onBack: PropTypes.func,
  onToggleLights: PropTypes.func,
  lightsEnabled: PropTypes.bool,
  hideNotch: PropTypes.bool,
  onOpenArtworkInfo: PropTypes.func,
  showArtworkInfoButton: PropTypes.bool,
  isArtworkInfoOpen: PropTypes.bool,
  showEnterOnVR: PropTypes.bool,
  enterOnVROpen: PropTypes.bool,
  onEnterOnVROpen: PropTypes.func,
  onEnterOnVRClose: PropTypes.func,
  linkCode: PropTypes.string,
  loadingLinkCode: PropTypes.bool,
  onEnterOnConnectedHeadset: PropTypes.func,
  attemptLink: PropTypes.func
};
